import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useRouteMatch,
} from "react-router-dom";
import React, {
  Children,
  FC,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from "react";

type Tab = {
  name: string;
  children?: ReactNode;
  className?: string;
  heading: ((props: { active: boolean }) => ReactNode) | ReactNode;
};

export const Tabs: FC<{
  className?: string;
  selected?: string;
  onSelect?: (name: string) => void;
}> = ({ children, className, onSelect, selected: initial }) => {
  const tabs = Children.map(
    children || [],
    (child) => (child as ReactElement<Tab>).props
  );
  const [selected, setSelected] = useState(
    initial || (tabs[0] && tabs[0].name) || undefined
  );

  useEffect(() => {
    initial && setSelected(initial);
  }, [initial]);

  return (
    <>
      <TabList
        className={className}
        onClick={(tab) => {
          setSelected(tab.name);
          onSelect && onSelect(tab.name);
        }}
        selected={selected}
        tabs={tabs}
      />

      {tabs.map((tab, i) => {
        return selected === tab.name && <TabPanel key={i} tab={tab} />;
      })}
    </>
  );
};

export const RouteTabs: FC<{
  className?: string;
  match: string;
  onSelect?: (name: string) => void;
}> = ({ children, className, match, onSelect }) => {
  const tabs = Children.map(
    children || [],
    (child) => (child as ReactElement<Tab>).props
  );

  const history = useHistory();
  const route = useRouteMatch<{ tab: string }>({ path: match });

  const url = (tab: Tab) => match.replace(":tab", tab.name);

  return (
    <>
      <TabList
        className={className}
        onClick={(tab) => {
          if (onSelect) {
            onSelect(tab.name);
          } else {
            history.push(url(tab));
          }
        }}
        selected={route?.params.tab}
        tabs={tabs}
      />

      <Switch>
        {tabs.map((tab, i) => (
          <Route key={i} path={url(tab)}>
            <TabPanel tab={tab} />
          </Route>
        ))}

        <Route>
          <Redirect to={url(tabs[0])} />
        </Route>
      </Switch>
    </>
  );
};

const TabList: FC<{
  className?: string;
  selected: string | undefined;
  tabs: Tab[];
  onClick: (tab: Tab) => void;
}> = ({ className, onClick, selected, tabs }) => {
  return (
    <ul className={`flex border-b h-14 ${className || ""}`} role="tablist">
      {tabs.map((tab, i) => {
        return (
          <li className="h-14" key={i}>
            <button
              aria-controls={aria_panel(tab.name)}
              aria-selected={selected === tab.name}
              className={`relative h-14 py-3 cursor-pointer border-b-2 select-none focus:outline-none focus:border-blue-700 hover:border-blue-700 ${
                selected === tab.name
                  ? "text-gray-800 border-blue-700"
                  : "text-gray-600 border-transparent-000"
              } ${tabs.length - 1 === i ? "" : "mr-10"}`}
              data-cy-tab={tab.name}
              id={aria_tab(tab.name)}
              onClick={() => onClick(tab)}
              role="tab"
              type="button"
            >
              {typeof tab.heading === "function"
                ? tab.heading({ active: selected === tab.name })
                : tab.heading}
            </button>
          </li>
        );
      })}
    </ul>
  );
};

const TabPanel: FC<{ tab: Tab }> = ({ tab }) => {
  if (!tab.children) {
    return null;
  }

  return (
    <div
      aria-labelledby={aria_tab(tab.name)}
      className={tab.className}
      id={aria_panel(tab.name)}
      role="tabpanel"
    >
      {tab.children}
    </div>
  );
};

export const Tab: FC<Tab> = ({ children }) => {
  return <>{children}</>;
};

function aria_panel(tab: string): string {
  return `tabs-${tab}-panel`;
}

function aria_tab(tab: string): string {
  return `tabs-${tab}-tab`;
}
